import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { Formik, Form } from "formik";
import { Eye, EyeOff } from "lucide-react";
import { useAuthStore } from "../hooks";
import { ResetPasswordSchema } from "../schemas";
import { PATHS } from "@/lib/router";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/common";
import { AuthStandaloneShell } from "../components";

const ResetPasswordPage = observer(() => {
  const navigate = useNavigate();
  const { t } = useTran();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    forgotRawToken,
    forgotOtpToken,
    loading,
    forgotReset,
    setTokenFromUrl,
    confirmResetToken,
    submitNewPassword,
  } = useAuthStore();

  useEffect(() => {
    setTokenFromUrl();
  }, [setTokenFromUrl]);

  useEffect(() => {
    if (forgotRawToken && !forgotOtpToken && !loading) {
      confirmResetToken();
    }
  }, [forgotRawToken, forgotOtpToken, loading, confirmResetToken]);

  const handleSubmit = async (
    values,
    { setErrors, setSubmitting, setTouched },
  ) => {
    const success = await submitNewPassword(values);
    setSubmitting(false);

    if (success) {
      navigate(PATHS.LOGIN, { replace: true });
      return;
    }

    if (forgotReset?.errors && Object.keys(forgotReset.errors).length > 0) {
      setErrors({ ...forgotReset.errors });
      setTouched({ password: true, confirmPassword: true }, true);
    }
  };

  return (
    <AuthStandaloneShell>
      <Formik
        initialValues={{ password: "", confirmPassword: "" }}
        validationSchema={ResetPasswordSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, touched, values, handleChange }) => (
          <Form className="flex flex-col items-center">
            <h1 className="mb-2 text-center text-2xl font-semibold text-primary sm:text-3xl">
              {t("Set New Password")}
            </h1>
            <p className="mb-6 text-center text-sm text-muted-foreground">
              {t("Create a strong password for your account.")}
            </p>

            {loading && !forgotOtpToken && (
              <p className="mb-4 text-center text-sm text-muted-foreground">
                {t("Verifying reset link...")}
              </p>
            )}

            <FormField
              id="new-password"
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder={t("New Password")}
              value={values.password}
              onChange={handleChange}
              error={touched.password && errors.password}
              disabled={loading || !forgotOtpToken}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword((state) => !state)}
                  className="text-gray-500"
                  aria-label={
                    showPassword ? t("Hide password") : t("Show password")
                  }
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              }
              className="mb-4 w-full"
            />

            <FormField
              id="confirm-password"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              placeholder={t("Confirm new password")}
              value={values.confirmPassword}
              onChange={handleChange}
              error={touched.confirmPassword && errors.confirmPassword}
              disabled={loading || !forgotOtpToken}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((state) => !state)}
                  className="text-gray-500"
                  aria-label={
                    showConfirmPassword
                      ? t("Hide password")
                      : t("Show password")
                  }
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              }
              className="mb-4 w-full"
            />

            {forgotReset?.serverError && (
              <p className="mb-4 text-center text-sm text-red-600">
                {forgotReset.serverError}
              </p>
            )}

            <Button
              type="submit"
              disabled={isSubmitting || loading || !forgotOtpToken}
              className="w-full rounded-full bg-primary hover:bg-primary/90"
            >
              {isSubmitting || loading
                ? t("Updating...")
                : t("Update Password")}
            </Button>

            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(PATHS.LOGIN)}
              className="mt-4 w-full"
            >
              {t("Back to Login")}
            </Button>
          </Form>
        )}
      </Formik>
    </AuthStandaloneShell>
  );
});

export default ResetPasswordPage;
